import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { costCurveApi } from "@/api/costCurve";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface CostCurveWidgetProps {
  /** YYYY-MM-DD; falls back to today if omitted. */
  date?: string;
}

export function CostCurveWidget({ date }: CostCurveWidgetProps) {
  const { t } = useTranslation();
  const [showChart, setShowChart] = useState(false);

  const cc = useQuery({
    queryKey: ["cost-curve", date ?? "today"],
    queryFn: () => costCurveApi.get(date),
  });

  if (cc.isLoading) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-slate-500">{t("common.loading")}</CardContent>
      </Card>
    );
  }
  if (cc.isError || !cc.data) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-slate-500">
          {t("cost_curve.unavailable")}
        </CardContent>
      </Card>
    );
  }

  const presets = cc.data.presets;
  const fmt = (n: number) =>
    n.toLocaleString("uk-UA", { maximumFractionDigits: 0 });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{t("cost_curve.title")}</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setShowChart((v) => !v)}>
            {showChart ? t("cost_curve.hide_chart") : t("cost_curve.show_chart")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-3 gap-3 text-sm">
          {(["50", "75", "100"] as const).map((k) => {
            const p = presets[k];
            if (!p) return null;
            return (
              <div key={k} className="rounded-lg bg-slate-50 p-3">
                <div className="text-xs text-slate-500">{k}% — {fmt(p.power_kw)} {t("common.kw")}</div>
                <div className="font-mono text-lg">{fmt(p.cost_uah_per_mwh)}</div>
                <div className="text-xs text-slate-500">{t("cost_curve.unit")}</div>
              </div>
            );
          })}
        </div>

        {showChart && (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={cc.data.points}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="power_kw"
                  type="number"
                  domain={[cc.data.power_min_kw, cc.data.power_max_kw]}
                  tickFormatter={(v) => fmt(v as number)}
                  label={{ value: t("optimizer.axis_power"), position: "insideBottom", offset: -2 }}
                />
                <YAxis tickFormatter={(v) => fmt(v as number)} />
                <Tooltip formatter={(v) => fmt(v as number)} />
                <Line type="monotone" dataKey="cost_uah_per_mwh" stroke="#2563eb" dot={false} />
                {presets["50"] && (
                  <ReferenceLine x={presets["50"].power_kw} stroke="#94a3b8" strokeDasharray="3 3" />
                )}
                {presets["75"] && (
                  <ReferenceLine x={presets["75"].power_kw} stroke="#94a3b8" strokeDasharray="3 3" />
                )}
                {presets["100"] && (
                  <ReferenceLine x={presets["100"].power_kw} stroke="#94a3b8" strokeDasharray="3 3" />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
